/**
 * Note: This is not the InfoWindow component. This is used has a child for a Map Marker
 * This component displays information about a site
 */
import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import Button from '../../../Button/Button';
import Icon from '../../../Icons/Icon';
import { getH2ANameFromH2AValue } from '../../../../utils/api/site';
import { formatLastVisit, formatNumericDate } from '../../../../utils/dates';
import { useVamosMap } from '../../../../hooks';

const SiteInfoWindow = ({
  site, addStop, isSmallMap = false, setIsZoomedToRoute = undefined,
}) => {
  // Checking which on is the Site/Stop @id and @name
  // Since this component is being reused for SitesInMap, SiteShow, and Routes component
  // They have the same information with different attributes/property name.
  const id = site.site_id || site.id;
  const name = site.name || site.site_name;
  const { setSelectedMarker } = useVamosMap();

  const containerClass = isSmallMap ? 'max-w-56' : 'max-w-75';
  const h2a = getH2ANameFromH2AValue(site.h2a) || 'No data';
  const lastVisit = formatLastVisit(site.last_visit);

  // State to handle loading bool
  const [loading, setLoading] = useState(false);

  const priorityClass = (() => {
    switch (site.priority) {
      case 'low':
        return 'bg-priority-low';
      case 'medium':
        return 'bg-priority-medium';
      case 'high':
        return 'bg-priority-high';
      default:
        return 'hidden';
    }
  })();

  return (
    <div className={`px-4 pb-3 ${containerClass}`}>
      <div className="mb-2 flex gap-2">
        <div className={`content-center ${priorityClass === 'hidden' ? 'hidden' : ''}`}>
          <div className={`w-4 h-4 rounded-full ${priorityClass}`} />
        </div>
        <div className={`font-sans font-bold text-vamos-green1 ${isSmallMap ? 'text-lg' : 'text-xl'}`}>
          <a href={`/sites/${id}`} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        </div>
      </div>
      <div className="mb-2">
        <div className="text-xs font-sans text-gray">{site?.city ? site?.city : site?.location?.city}</div>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 font-sans text-sm font-normal">
        {lastVisit && (
          <>
            <div className="text-vamos-gray">LAST VISIT</div>
            <div className="text-vamos-black">{lastVisit}</div>
          </>
        )}
        {site.arrival && (
          <>
            <div className="text-vamos-gray">ARRIVAL</div>
            <div className="text-vamos-black">{formatNumericDate(site.arrival)}</div>
          </>
        )}
        {site.departure && (
          <>
            <div className="text-vamos-gray">DEPARTURE</div>
            <div className="text-vamos-black">{formatNumericDate(site.departure)}</div>
          </>
        )}
        {site.worker_count && (
          <>
            <div className="text-vamos-gray">WORKER COUNT</div>
            <div className="text-vamos-black">{site.worker_count}</div>
          </>
        )}
        {h2a && (
          <>
            <div className="text-vamos-gray">H2A</div>
            <div className="text-vamos-black">{h2a}</div>
          </>
        )}
      </div>
      <div className="flex justify-center gap-3 mt-4">
        <div className="flex-1 flex justify-start">
          <Button
            label="Details"
            Icon={<Icon name="open_in_new" />}
            color="secondary"
            size="md"
            onClick={() => window.open(`/sites/${id}`, '_blank', 'noreferrer')}
          />
        </div>
        {addStop && (
          <div className="flex-1 flex justify-start">
            <Button
              label={loading ? 'Saving...' : 'Add'}
              Icon={<Icon name="plus" />}
              color="secondary"
              size="md"
              onClick={() => {
                addStop(site, setLoading, setSelectedMarker, setIsZoomedToRoute);
              }}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

SiteInfoWindow.propTypes = {
  site: PropTypes.object.isRequired,
  addStop: PropTypes.func,
  isSmallMap: PropTypes.bool,
  setIsZoomedToRoute: PropTypes.func,
};

export default SiteInfoWindow;
